import { Badge } from "@qw/ui";
import type { FC } from "react";
import type { ProductHistoricalInsights } from "../historical-insights.types";
import { formatPercent } from "../insights-utils";

interface Props {
  carrierContext: ProductHistoricalInsights["carrierContext"];
}

const tierVariant = (tier: string | null) => {
  switch (tier?.toLowerCase()) {
    case "target":
    case "preferred":
      return "green" as const;
    case "acceptable":
    case "standard":
      return "default" as const;
    case "conditional":
      return "amber" as const;
    case "avoid":
    case "decline":
      return "destructive" as const;
    default:
      return "secondary" as const;
  }
};

export const InsightsCarrierContextSection: FC<Props> = ({ carrierContext }) => {
  const hasData =
    carrierContext.appetiteTier != null ||
    carrierContext.carrierSummary != null ||
    carrierContext.redFlags.length > 0 ||
    carrierContext.exclusions.length > 0;

  if (!hasData) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold">Carrier Context</h3>

      <div className="flex flex-wrap gap-2">
        {carrierContext.appetiteTier && (
          <Badge variant={tierVariant(carrierContext.appetiteTier)}>{carrierContext.appetiteTier}</Badge>
        )}
        {carrierContext.appetiteConfidence != null && (
          <Badge variant="secondary">Confidence: {formatPercent(carrierContext.appetiteConfidence)}</Badge>
        )}
        {carrierContext.pricingTendency && <Badge variant="secondary">Pricing: {carrierContext.pricingTendency}</Badge>}
      </div>

      {carrierContext.carrierSummary && (
        <p className="text-xs text-muted-foreground">{carrierContext.carrierSummary}</p>
      )}

      {carrierContext.redFlags.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">Red Flags</h4>
          <ul className="list-disc list-inside text-xs space-y-0.5">
            {carrierContext.redFlags.map((flag, i) => (
              <li key={i}>{flag}</li>
            ))}
          </ul>
        </div>
      )}

      {carrierContext.exclusions.length > 0 && (
        <div>
          <h4 className="text-xs font-medium text-muted-foreground mb-1">Exclusions</h4>
          <ul className="list-disc list-inside text-xs space-y-0.5">
            {carrierContext.exclusions.map((exclusion, i) => (
              <li key={i}>{exclusion}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
